// ============================================================================
// BESPOKIAN Health - Batch Processing Page
// Bulk operations for prescriptions - efficiency for high-volume pharmacies
// ============================================================================

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Layers,
  Filter,
  Play,
  Square,
  RefreshCw,
  Package,
  FileCheck,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { queueApi } from '@/api/queue.api';
import { PrescriptionStatus, Prescription } from '@/types';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge, Alert, Spinner } from '@/components/common/ui';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ControlledSubstanceBadge } from '@/components/common/ControlledSubstanceBadge';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

type BatchOperation = 'verify' | 'fill' | 'check';

interface BatchResult {
  prescriptionId: string;
  rxNumber: string;
  success: boolean;
  error?: string;
}

interface OperationConfig {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  pharmacistOnly?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function BatchProcessingPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isPharmacist = user?.role === 'PHARMACIST';

  // State
  const [selectedOperation, setSelectedOperation] = useState<BatchOperation>('verify');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [excludeControlled, setExcludeControlled] = useState(true);
  const [results, setResults] = useState<BatchResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Determine status filter based on operation
  const statusFilter = useMemo(() => {
    switch (selectedOperation) {
      case 'verify':
        return 'PENDING_VERIFICATION';
      case 'fill':
        return 'PENDING_FILL';
      case 'check':
        return 'PENDING_PHARMACIST_CHECK';
      default:
        return 'PENDING_VERIFICATION';
    }
  }, [selectedOperation]);

  // Fetch eligible prescriptions
  const { data: prescriptions, isLoading, refetch } = useQuery({
    queryKey: ['queue', statusFilter, 'batch'],
    queryFn: () => queueApi.getQueue({ status: statusFilter as PrescriptionStatus }),
    select: (response) => {
      const data = response.data || response;
      const items = Array.isArray(data) ? data : [];
      if (excludeControlled) {
        return items.filter((rx: Prescription) => !rx.deaSchedule);
      }
      return items;
    },
  });

  // Convert API response to BatchResult format
  const convertToResults = (
    apiResponse: { success: string[]; failed: string[] },
    prescriptionList: Prescription[] | undefined
  ): BatchResult[] => {
    const resultsList: BatchResult[] = [];
    
    for (const id of apiResponse.success) {
      const rx = prescriptionList?.find(p => p.id === id);
      resultsList.push({
        prescriptionId: id,
        rxNumber: rx?.rxNumber || id.slice(0, 8),
        success: true,
      });
    }
    
    for (const id of apiResponse.failed) {
      const rx = prescriptionList?.find(p => p.id === id);
      resultsList.push({
        prescriptionId: id,
        rxNumber: rx?.rxNumber || id.slice(0, 8),
        success: false,
        error: 'Operation failed',
      });
    }
    
    return resultsList;
  };

  // Batch verify mutation
  const batchVerifyMutation = useMutation({
    mutationFn: (ids: string[]) => queueApi.batchVerify(ids),
    onSuccess: (data) => {
      const batchResults = convertToResults(data, prescriptions);
      setResults(batchResults);
      setShowResults(true);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      queryClient.invalidateQueries({ queryKey: ['queueCounts'] });
      toast.success(`Batch verify complete: ${data.success.length} succeeded`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Batch operation failed');
    },
  });

  // Batch fill mutation
  const batchFillMutation = useMutation({
    mutationFn: (ids: string[]) => {
      // For batch fill, we need to auto-select inventory lots
      // In a real implementation, this would query available lots
      const items = ids.map(id => ({
        prescriptionId: id,
        lotId: 'auto', // Auto-select FEFO lot
        quantity: 0, // Will use prescribed quantity
      }));
      return queueApi.batchFill(items);
    },
    onSuccess: (data) => {
      const batchResults = convertToResults(data, prescriptions);
      setResults(batchResults);
      setShowResults(true);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      queryClient.invalidateQueries({ queryKey: ['queueCounts'] });
      toast.success(`Batch fill complete: ${data.success.length} succeeded`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Batch operation failed');
    },
  });

  // Batch check mutation (pharmacist only)
  const batchCheckMutation = useMutation({
    mutationFn: (ids: string[]) => queueApi.batchCheck(ids),
    onSuccess: (data) => {
      const batchResults = convertToResults(data, prescriptions);
      setResults(batchResults);
      setShowResults(true);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      queryClient.invalidateQueries({ queryKey: ['queueCounts'] });
      toast.success(`Batch check complete: ${data.success.length} succeeded`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Batch operation failed');
    },
  });

  const isPending = batchVerifyMutation.isPending || batchFillMutation.isPending || batchCheckMutation.isPending;

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all
  const selectAll = () => {
    if (prescriptions) {
      setSelectedIds(new Set(prescriptions.map((rx: Prescription) => rx.id)));
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Execute batch operation
  const executeBatch = () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error('Select at least one prescription');
      return;
    }

    switch (selectedOperation) {
      case 'verify':
        batchVerifyMutation.mutate(ids);
        break;
      case 'fill':
        batchFillMutation.mutate(ids);
        break;
      case 'check':
        if (!isPharmacist) {
          toast.error('Only pharmacists can perform batch check');
          return;
        }
        batchCheckMutation.mutate(ids);
        break;
    }
  };

  // Operation config
  const operationConfig: Record<BatchOperation, OperationConfig> = {
    verify: {
      label: 'Batch Verify',
      description: 'Verify multiple prescriptions at once',
      icon: <FileCheck className="h-5 w-5" />,
      color: 'text-blue-600',
    },
    fill: {
      label: 'Batch Fill',
      description: 'Auto-fill using FEFO inventory selection',
      icon: <Package className="h-5 w-5" />,
      color: 'text-green-600',
    },
    check: {
      label: 'Batch Check',
      description: 'Pharmacist approval for multiple prescriptions',
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'text-purple-600',
      pharmacistOnly: true,
    },
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Batch Processing</h1>
          <p className="text-gray-600 mt-1">Process multiple prescriptions efficiently</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Operation Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Select Operation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            {(Object.keys(operationConfig) as BatchOperation[]).map(op => {
              const config = operationConfig[op];
              const isSelected = selectedOperation === op;
              const isDisabled = config.pharmacistOnly && !isPharmacist;

              return (
                <button
                  key={op}
                  onClick={() => {
                    if (!isDisabled) {
                      setSelectedOperation(op);
                      setSelectedIds(new Set());
                      setShowResults(false);
                    }
                  }}
                  disabled={isDisabled}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : isDisabled
                      ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`flex items-center gap-2 ${config.color}`}>
                    {config.icon}
                    <span className="font-medium">{config.label}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{config.description}</p>
                  {config.pharmacistOnly && !isPharmacist && (
                    <Badge variant="warning" className="mt-2">Pharmacist Only</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={excludeControlled}
              onChange={(e) => setExcludeControlled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-gray-700">
              Exclude controlled substances (C-II through C-V)
            </span>
          </label>
          <p className="text-sm text-gray-500 mt-2">
            Controlled substances require individual verification and cannot be batch processed.
          </p>
        </CardContent>
      </Card>

      {/* Selection Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-600">
            {selectedIds.size} of {prescriptions?.length || 0} selected
          </span>
          <Button variant="ghost" onClick={selectAll} size="sm">
            Select All
          </Button>
          <Button variant="ghost" onClick={clearSelection} size="sm">
            Clear
          </Button>
        </div>
        <Button
          variant="primary"
          onClick={executeBatch}
          disabled={selectedIds.size === 0 || isPending}
        >
          {isPending ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Processing...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Execute {operationConfig[selectedOperation].label}
            </>
          )}
        </Button>
      </div>

      {/* Prescription List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Spinner size="lg" />
        </div>
      ) : prescriptions && prescriptions.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {prescriptions.map((rx: Prescription) => (
                <label
                  key={rx.id}
                  className={`flex items-center gap-4 p-4 cursor-pointer transition-colors ${
                    selectedIds.has(rx.id)
                      ? 'bg-blue-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(rx.id)}
                    onChange={() => toggleSelect(rx.id)}
                    className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {rx.patient?.lastName || rx.patientName}, {rx.patient?.firstName || ''}
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-sm text-gray-600">Rx #{rx.rxNumber}</span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {rx.drugName} {rx.strength}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={rx.status} />
                    {rx.deaSchedule && (
                      <ControlledSubstanceBadge schedule={rx.deaSchedule} />
                    )}
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="h-4 w-4 mr-1" />
                      {rx.receivedAt ? Math.round(
                        (Date.now() - new Date(rx.receivedAt).getTime()) / 60000
                      ) : 0}m
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Square className="h-12 w-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No prescriptions</h3>
            <p className="text-gray-600 mt-1">
              No prescriptions available for {operationConfig[selectedOperation].label.toLowerCase()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {showResults && results.length > 0 && (
        <Card>
          <CardHeader>
            <button
              onClick={() => setShowResults(!showResults)}
              className="flex items-center justify-between w-full"
            >
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Batch Results
              </CardTitle>
              {showResults ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex items-center gap-4">
              <Badge variant="success">
                {results.filter(r => r.success).length} Succeeded
              </Badge>
              {results.some(r => !r.success) && (
                <Badge variant="danger">
                  {results.filter(r => !r.success).length} Failed
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              {results.map(result => (
                <div
                  key={result.prescriptionId}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    result.success ? 'bg-green-50' : 'bg-red-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                    <span className="font-medium">Rx #{result.rxNumber}</span>
                  </div>
                  {!result.success && result.error && (
                    <span className="text-sm text-red-600">{result.error}</span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Warning for controlled substances */}
      {!excludeControlled && (
        <Alert variant="warning">
          <AlertTriangle className="h-5 w-5" />
          <div>
            <strong>Controlled Substance Warning:</strong> Batch processing controlled
            substances bypasses individual verification requirements. This may not comply
            with state regulations. Use with caution.
          </div>
        </Alert>
      )}
    </div>
  );
}
